/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { FileType } from 'src/core/types/FileType';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerSynonymStore } from 'src/qnaMaker/core/stores/QnaMakerSynonymStore';
import { qnaMakerSynonymSwaggerToSynonymStore } from 'src/qnaMaker/modules/handlers/qnas/qnaMakerAssetsMappers';

export type SavingState = 'dirty' | 'saved' | 'inProgress';

const emptySynonym: Partial<QnaMakerSynonymSwagger> = {
    id: getRandomNumber(),
    alterations: []
};

export class QnaMakerSynonymsUiStore {
    @observable public synonymsBeingEditedId: number;

    @observable.shallow public SynonymsJson: QnaMakerSynonymSwagger[];

    @observable.shallow public FetchedSynonymsJson: QnaMakerSynonymSwagger[];

    @observable public displayedSynonymStores: QnaMakerSynonymStore[];

    @observable public synonymToEditId: number;

    @observable public savingState: SavingState;

    @observable public searchQuery: string;

    @observable public isAddSynonymOn: boolean;

    @observable public selectedSynonymId: number;

    @observable public importProjectModalSettings: { hidden: boolean; fileType: Extract<FileType, 'xlsx' | 'tsv'> };

    @observable public deleteModalSettings: { hidden: boolean; operation: 'synonym' };

    constructor(_synonymsJson: QnaMakerSynonymSwagger[] = []) {
        this.savingState = 'saved';
        this.searchQuery = '';
        this.displayedSynonymStores = [];
        this.SynonymsJson = _synonymsJson ?? [];
        this.isAddSynonymOn = false;
        this.importProjectModalSettings = { hidden: true, fileType: null };
        this.deleteModalSettings = { hidden: true, operation: null };
        this.synonymsBeingEditedId = null;
        this.selectedSynonymId = null;
    }

    @action
    public setSearchQuery(searchQuery: string) {
        this.searchQuery = searchQuery;
    }

    @action
    public setSynonymsJson(_synonymsJson: QnaMakerSynonymSwagger[]) {
        this.SynonymsJson = [..._synonymsJson];
    }

    @action
    public setFetchedSynonymsJson(_fetchedSynonymsJson: QnaMakerSynonymSwagger[]) {
        this.FetchedSynonymsJson = [..._fetchedSynonymsJson];
    }

    @action
    public setDisplayedSynonymStores(_displayedSynonymStores: QnaMakerSynonymStore[]) {
        this.displayedSynonymStores = _displayedSynonymStores;
    }

    @action
    public setSavingState(state: SavingState) {
        this.savingState = state;
    }

    @action
    public setIsAddSynonymOn(isAddSynonymOn: boolean) {
        this.isAddSynonymOn = isAddSynonymOn;
    }

    @action
    public setSynonymsBeingEditedId(id: number) {
        const found = this.displayedSynonymStores.find(s => s.id === id);
        if (found) {
            this.synonymsBeingEditedId = id;
        }
    }

    @action
    public clearSynonymsBeingEditedId() {
        this.synonymsBeingEditedId = null;
    }

    @action
    public getSynonymsBeingEdited(id: number) {
        if (this.synonymsBeingEditedId === id) {
            return this.displayedSynonymStores.find(s => s.id === id);
        }
    }

    @action
    public setSelectedSynonymId(id: number) {
        this.selectedSynonymId = id;
    }

    @action
    public setDeleteModalSettings(settings: Partial<{ hidden: boolean; operation: 'synonym' }>) {
        this.deleteModalSettings = { ...this.deleteModalSettings, ...settings };
    }

    @action
    public setImportProjectModalSettings(settings: Partial<{ hidden: boolean; fileType: Extract<FileType, 'xlsx' | 'tsv'> }>) {
        this.importProjectModalSettings = { ...this.importProjectModalSettings, ...settings };
    }

    @action
    public deleteDisplayedSynonyms(ids: number[]) {
        this.SynonymsJson = this.SynonymsJson.filter(synonym => !ids.includes(synonym.id));

        this.displayedSynonymStores = this.displayedSynonymStores
            .filter(synonym => !ids.includes(synonym.id))
            .map(p => new QnaMakerSynonymStore(this.SynonymsJson.find(s => s.id === p.id)));
    }

    @action
    public addDisplayedSynonyms(synonymData: QnaMakerSynonymSwagger) {
        const synonymToAdd: QnaMakerSynonymSwagger = {
            ...emptySynonym,
            ...synonymData
        };

        this.SynonymsJson = [synonymToAdd, ...this.SynonymsJson];
        this.displayedSynonymStores = [
            new QnaMakerSynonymStore({ id: synonymToAdd.id, alterations: synonymToAdd.alterations }),
            ...this.displayedSynonymStores
        ];
    }

    @action
    public editDisplayedSynonym(synonymId: number, newData: Partial<QnaMakerSynonymSwagger>) {
        let updatedSynonym: QnaMakerSynonymSwagger;

        this.SynonymsJson = this.SynonymsJson.map(synonym => {
            if (synonym.id === synonymId) {
                updatedSynonym = { ...synonym, ...newData };

                return updatedSynonym;
            }

            return synonym;
        });

        this.displayedSynonymStores = this.displayedSynonymStores.map(p =>
            qnaMakerSynonymSwaggerToSynonymStore(this.SynonymsJson.find(s => s.id === p.id))
        );
    }

    @action
    public resetSynonym() {
        this.SynonymsJson = [...this.FetchedSynonymsJson];

        this.displayedSynonymStores = this.SynonymsJson.map(p => qnaMakerSynonymSwaggerToSynonymStore(p));

        this.savingState = 'saved';
    }
}
